<template>
  <div>

    <div class="panel panel-default">
      <div
        class="panel-heading"
        role="button"
        :aria-expanded="showPartnerDetails"
        @click="togglePartnerDetails"
      >
        <strong>Partner Connection Details</strong>
        <i
          class="fa pull-right"
          :class="showPartnerDetails ? 'fa-caret-down' : 'fa-caret-right'"
        />
      </div>
      <div v-if="showPartnerDetails" class="panel-body">
        <p class="help-block">
          When establishing an SFTP connection with a partner, share these connection details with them: <br/>
          <b>Hostname:</b> <code>sftp.goalbookapp.com</code><br/>
          <b>Port:</b> <code>22</code><br/>
          <b>Protocol:</b> <code>SFTP</code><br/>
          <b>Username:</b> <code>{{ localUser.username }}</code><br/>
          <b>Home Directory:</b> <code>{{ localUser.home_directory }}</code>
        </p>
        <p class="help-block">
          The partner will need to create a public/private key pair. The public key should be added to the <code>ssh_public_key</code> field above. The private key should be used to authenticate the SFTP connection. They key must be in one of the following <a href="https://docs.aws.amazon.com/transfer/latest/userguide/key-management.html#ssh-authentication-algorithms">formats</a>:
        </p>
        <ul>
          <li>rsa-sha2-256</li>
          <li>rsa-sha2-512</li>
          <li>ssh-rsa</li>
        </ul>
      </div>
    </div>

    <div class="form-group">
      <label for="tf-username">Username</label>
      <input
        id="tf-username"
        v-model="localUser.username"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': !localUser.username.trim() && showValidationErrors }"
      >
    </div>

    <div class="form-group">
      <label for="tf-home-directory">Home Directory</label>
      <input
        id="tf-home-directory"
        v-model="localUser.home_directory"
        type="text"
        class="form-control"
        :placeholder="'/goalbook-sftp-dev/'+ localUser.username"
        :class="{ 'is-invalid': !localUser.home_directory.trim() && showValidationErrors }"
      >
      <p class="help-block">
        Full S3 path starting with <code>/</code>, for example <code>/goalbook-sftp-dev/{{ localUser.username }}</code>.
      </p>
    </div>

    <div class="form-group">
      <label for="tf-ssh-key">SSH Public Key</label>
      
      <!-- Non-editable code display (default) -->
      <div v-if="!isEditingKey" class="panel panel-default">
        <div class="panel-body">
          <div class="row">
            <div class="col-xs-10">
              <div v-if="localUser.ssh_public_key.trim()">
                <pre>{{ localUser.ssh_public_key }}</pre>
              </div>
              <span v-else class="text-muted">
                No SSH public key set
              </span>
            </div>
            <div class="col-xs-2 text-right">
              <button 
                type="button" 
                class="btn btn-sm btn-default"
                @click="startEditingKey"
              >
                <i class="fa fa-edit"></i> Edit
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Editable textarea (when editing) -->
      <div v-else class="panel panel-default">
        <div class="panel-body">
          <textarea
            id="tf-ssh-key"
            v-model="localUser.ssh_public_key"
            class="form-control"
            rows="4"
            ref="sshKeyTextarea"
          />
          <div class="text-right margin-top-10">
            <button 
              type="button" 
              class="btn btn-sm btn-primary"
              @click="saveKeyEdit"
            >
              <i class="fa fa-check"></i> Save
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-default margin-left-5"
              @click="cancelKeyEdit"
            >
              <i class="fa fa-times"></i> Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="form-group">
      <button type="button" class="btn btn-primary" @click="onSubmit">
        {{ submitLabel }}
      </button>
      <button type="button" class="btn btn-default" @click="$emit('cancel')">
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TransferFamilyUserForm',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'create' // 'create' | 'edit'
    }
  },
  data() {
    return {
      localUser: {
        username: this.value.username || '',
        home_directory: this.value.home_directory || '',
        ssh_public_key: this.value.ssh_public_key || ''
      },
      showPartnerDetails: false,
      isEditingKey: false,
      originalKey: this.value.ssh_public_key || '',
      showValidationErrors: false
    }
  },
  computed: {
    submitLabel() {
      return this.mode === 'edit' ? 'Save' : 'Create'
    },
    isFormValid() {
      return this.localUser.username.trim() && this.localUser.home_directory.trim()
    }
  },
  methods: {
    togglePartnerDetails() {
      this.showPartnerDetails = !this.showPartnerDetails
    },
    startEditingKey() {
      this.originalKey = this.localUser.ssh_public_key
      this.isEditingKey = true
      // Focus the textarea after it's rendered
      this.$nextTick(() => {
        if (this.$refs.sshKeyTextarea) {
          this.$refs.sshKeyTextarea.focus()
        }
      })
    },
    saveKeyEdit() {
      this.isEditingKey = false
    },
    cancelKeyEdit() {
      this.localUser.ssh_public_key = this.originalKey
      this.isEditingKey = false
    },
    onSubmit() {
      if (!this.isFormValid) {
        this.showValidationErrors = true
        return
      }
      this.showValidationErrors = false
      this.$emit('submit', {
        username: this.localUser.username,
        home_directory: this.localUser.home_directory,
        ssh_public_key: this.localUser.ssh_public_key
      })
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

.margin-top-10 {
  margin-top: 10px;
}

.margin-left-5 {
  margin-left: 5px;
}
</style>

